import { ValidatePassword, ValidatePasswordMessage } from "@swivl-mono/helpers";
import { AccountInterface } from "@swivl-mono/types";
import { Button, ButtonColor, ButtonSize, DEFAULT_AVATAR, ImageUploader, ImageUploaderType, Input, PasswordInput } from "@swivl-mono/ui";
import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import AccountModel from "../../../Models/Account/AccountModel";
import { AlertsModel } from "../../../Models/Notifications/Alerts.Model";
import Logo from "../Assets/SwivlLogo.png";

interface AccountWithPassword extends Omit<AccountInterface, "id" | "username" | "createdAt" | "updatedAt"> {
  password: string;
  avatarURL: string;
}
export function SignUpView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { invites } = AccountModel.useState();
  const navigate = useNavigate();
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [account, setAccount] = React.useState<AccountWithPassword>({
    firstName: "",
    lastName: "",
    email: "",
    avatarURL: DEFAULT_AVATAR,
    password: ""
  });
  const inviteUUID = searchParams.get("invite");

  useEffect(() => {
    if (inviteUUID) {
      if (invites) {
        const invite = invites.find((i) => i.uuid === inviteUUID);

        if (invite) {
          setAccount({
            ...account,
            firstName: invite.firstName,
            lastName: invite.lastName,
            email: invite.email
          });
        }
      } else {
        AccountModel.loadInvite(inviteUUID);
      }
    }
  }, [inviteUUID, invites]);

  const submit = async () => {
    if (!account.email) {
      setError("Email is required");
      return;
    }
    if (!account.firstName) {
      setError("First Name is required");
      return;
    }
    if (!account.lastName) {
      setError("Last Name is required");
      return;
    }
    if (!account.password) {
      setError("Password is required");
      return;
    }

    const isValidPassword = ValidatePassword(account.password);
    if (isValidPassword === false) {
      setError(ValidatePasswordMessage);
      return;
    }
    setSaving(true);

    AccountModel.signUp(
      account.firstName,
      account.lastName,
      account.email,
      account.avatarURL,
      account.password,
      inviteUUID as string | undefined,
      navigate
    )
      // .then((account) => {})
      .catch((error) => {
        if (error.message) {
          if (error.message === "Username is taken") {
            navigate("/login");
            AlertsModel.errorToast("An account already exists with this email, please log in");
            return;
          }
          setError(error.message);
          if (error.message === "Email already exists") {
            setError("An account already exists with this email");
          }
        } else {
          setError("Account Creation Failed");
        }
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <div className="SignUpView flex flex-col items-center">
      <img src={Logo} alt="Swivl Logo" className="mx-auto mb-8 w-1/2" />

      <h2 className="heading-lg mb-4">Welcome</h2>
      <div className="container mb-3 flex flex-row items-center justify-center">
        <ImageUploader
          isEditable={true}
          imageURL={account.avatarURL}
          type={ImageUploaderType.AVATAR}
          imageUploaded={(url) => {
            setAccount((prevAccount) => ({ ...prevAccount, avatarURL: url }));
          }}
          size={120}
          round
        />
      </div>
      <div className="container mx-auto mb-5 flex flex-col gap-2">
        <Input
          label="Email"
          placeholder="Email"
          name="email"
          type="email"
          value={account.email}
          onChange={(e) => setAccount({ ...account, email: e.target.value })}
        />
        <div className="flex gap-2">
          <Input
            label="First Name"
            placeholder="First Name"
            name="firstName"
            value={account.firstName}
            containerClassName="flex-1"
            onChange={(e) => setAccount({ ...account, firstName: e.target.value })}
          />
          <Input
            label="Last Name"
            placeholder="Last Name"
            name="lastName"
            value={account.lastName}
            containerClassName="flex-1"
            onChange={(e) => setAccount({ ...account, lastName: e.target.value })}
          />
        </div>
        <PasswordInput
          label="Password"
          name="password"
          placeholder="Password"
          value={account.password}
          autoComplete="new-password"
          onChange={(e) => setAccount({ ...account, password: e.target.value })}
        />
      </div>
      {error && <div className="mb-4 text-center text-red-500">{error}</div>}

      <Button loading={saving} onClick={submit} color={ButtonColor.ORANGE}>
        Submit
      </Button>
    </div>
  );
}
